/**
 * Extrae un mensaje de error para que sea legible desde respuestas API.
 *
 * Soporta casos como:
 * - { message: "Error simple" }
 * - { messages: { field: ["msg1", "msg2"] } }
 * - { messages: ["msg1", "msg2"] }
 * - Axios error: err.response.data
 *
 * @param {unknown} source - Puede ser `err`, `err.response`, `err.response.data` o `data`
 * @param {string} fallback - Mensaje por defecto si no se puede inferir uno
 * @returns {string}
 */
export const getApiErrorMessage = (
  source,
  fallback = 'Sucedió un error inesperado, por favor inténtalo nuevamente.'
) => {
  // Normaliza posibles entradas (axios error, response o data directo)
  const data = source?.response?.data ?? source?.data ?? source ?? null

  if (!data) return fallback

  // 1) message directo string
  if (typeof data.message === 'string' && data.message.trim()) {
    return data.message.trim()
  }

  // 2) messages como arreglo
  if (Array.isArray(data.messages)) {
    const joined = data.messages
      .flat(Infinity)
      .filter(Boolean)
      .map(String)
      .map((s) => s.trim())
      .filter(Boolean)
      .join(' ')
    if (joined) return joined
  }

  // 3) messages como objeto (ej: { email: ["..."], password: ["..."] })
  if (data.messages && typeof data.messages === 'object') {
    const collected = []

    for (const value of Object.values(data.messages)) {
      if (Array.isArray(value)) {
        collected.push(...value)
      } else if (value != null) {
        collected.push(value)
      }
    }

    const joined = collected
      .flat(Infinity)
      .filter(Boolean)
      .map(String)
      .map((s) => s.trim())
      .filter(Boolean)
      .join(' ')

    if (joined) return joined
  }

  return fallback
}